import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Menu } from '@parkify/ui'
import { sendRegisterNudge } from '../lib/api'
import { ghostBtn, purpleBtn } from '../ui'

/**
 * "Add parker": a menu button on the shared @parkify/ui `Menu`, so it has the
 * same keyboard contract as every other menu (ROW-PATTERN-001 step 2, demo
 * #106): Enter, Space and ArrowDown open it on the first item, arrows move and
 * wrap, Escape closes and returns focus to the trigger. Pointer behaviour is
 * unchanged.
 *
 * "Invite to register" is not a leaf action: it swaps the menu for the invite
 * panel anchored in the same place. That panel is not a menu, so it keeps its
 * own dismissal (outside pointerdown, Escape) and returns focus to the trigger
 * on Escape.
 */
export function AddParkerActions({
  token, onAddManually, buttonStyle = ghostBtn, namedPermits,
}: {
  token: string
  onAddManually: () => void
  /** Named permits (operator setting): the invite names one of these. */
  namedPermits?: NamedPermitOption[] | null
  buttonStyle?: CSSProperties
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!inviteOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setInviteOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setInviteOpen(false)
      rootRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [inviteOpen])

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <Menu
        align="right"
        minWidth={190}
        open={menuOpen}
        onOpenChange={open => { setMenuOpen(open); if (open) setInviteOpen(false) }}
        items={[
          { key: 'manual', label: 'Add manually', onSelect: onAddManually },
          { key: 'invite', label: 'Invite to register', onSelect: () => setInviteOpen(true) },
        ]}
        renderTrigger={t => (
          <button {...t} style={buttonStyle}>Add parker</button>
        )}
      />
      {inviteOpen && <InviteToRegisterPanel token={token} namedPermits={namedPermits} onBack={() => { setInviteOpen(false); setMenuOpen(true) }} />}
    </div>
  )
}

export interface NamedPermitOption { id: string; label: string }

function InviteToRegisterPanel({ token, onBack, namedPermits }: { token: string; onBack: () => void; namedPermits?: NamedPermitOption[] | null }) {
  const [email, setEmail] = useState('')
  const [permitId, setPermitId] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const send = async () => {
    const clean = email.trim()
    if (!clean || busy || (namedPermits && !permitId)) return
    setBusy(true); setError(''); setNotice('')
    try {
      const res = await sendRegisterNudge(token, { email: clean, ...(namedPermits ? { permitId } : {}) })
      setNotice(res.message || `Invite sent to ${clean}.`)
      setEmail('')
    } catch (e) {
      // The server's own words: it distinguishes "already invited recently"
      // from "couldn't send", and both are things the manager can act on.
      setError(e instanceof Error ? e.message : 'Could not send that invite.')
    } finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 20, width: 300, padding: 12, background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 10, boxShadow: '0 12px 30px rgba(0,0,0,0.14)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button onClick={onBack} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, color: '#7F3F98' }}>← Back</button>
        <div style={{ fontSize: 12.5, fontWeight: 800 }}>Invite to register</div>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', lineHeight: 1.5, marginBottom: 9 }}>
        We'll email them a link to the sign-up form. They still need your approval afterwards.
      </div>
      {namedPermits && (
        <select aria-label="Permit" value={permitId} onChange={e => setPermitId(e.target.value)}
          style={{ width: '100%', marginBottom: 6, fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontFamily: 'inherit' }}>
          <option value="">Which permit?</option>
          {namedPermits.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void send() }}
          placeholder="colleague@work.com"
          type="email"
          aria-label="Colleague's email address"
          style={{ flex: 1, minWidth: 0, fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontFamily: 'inherit' }}
        />
        <button
          onClick={() => void send()}
          disabled={busy || !email.trim() || (!!namedPermits && !permitId)}
          style={{ ...purpleBtn, padding: '7px 12px', fontSize: 12.5, opacity: busy || !email.trim() || (!!namedPermits && !permitId) ? 0.5 : 1 }}
        >{busy ? 'Sending…' : 'Send'}</button>
      </div>
      {notice && <div role="status" style={{ fontSize: 12, color: '#15803d', marginTop: 8, lineHeight: 1.5 }}>{notice}</div>}
      {error && <div role="alert" style={{ fontSize: 12, color: '#b91c1c', marginTop: 8, lineHeight: 1.5 }}>{error}</div>}
    </div>
  )
}
